const MAX_PLACES = 8;

const asTrimmedString = (value, maxLength = 160) => (
  typeof value === 'string' ? value.trim().slice(0, maxLength) : ''
);

const asFiniteNumber = (value) => (
  typeof value === 'number' && Number.isFinite(value) ? value : null
);

const asHttpUrl = (value, maxLength = 500) => {
  const url = asTrimmedString(value, maxLength);
  if (!url) return null;
  if (url.startsWith('//')) return `https:${url}`.slice(0, maxLength);
  return /^https?:\/\//i.test(url) ? url : null;
};

const parseGps = (value) => {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return null;
  const lat = asFiniteNumber(value.lat ?? value.latitude);
  const lng = asFiniteNumber(value.lng ?? value.longitude);
  if (lat === null || lng === null) return null;
  if (lat < -90 || lat > 90 || lng < -180 || lng > 180) return null;
  return { lat, lng };
};

const parsePlace = (item, index) => {
  if (!item || typeof item !== 'object' || Array.isArray(item)) return null;
  const title = asTrimmedString(item.title, 200);
  if (!title) return null;

  const thumbnails = Array.isArray(item.thumbnails)
    ? item.thumbnails.map((url) => asHttpUrl(url)).filter(Boolean).slice(0, 6)
    : [];
  const thumbnail = asHttpUrl(item.thumbnail) || thumbnails[0] || null;
  if (thumbnail && !thumbnails.includes(thumbnail)) thumbnails.unshift(thumbnail);

  return {
    title,
    address: asTrimmedString(item.address, 240) || null,
    rating: asFiniteNumber(item.rating),
    reviews: asFiniteNumber(item.reviews),
    type: asTrimmedString(item.type, 80) || null,
    phone: asTrimmedString(item.phone, 40) || null,
    website: asHttpUrl(item.website),
    openState: asTrimmedString(item.openState || item.open_state, 80) || null,
    hours: asTrimmedString(item.hours, 120) || null,
    price: asTrimmedString(item.price, 20) || null,
    thumbnail,
    thumbnails,
    link: asHttpUrl(item.link, 500),
    placeId: asTrimmedString(item.placeId || item.place_id, 80) || null,
    position: asFiniteNumber(item.position) ?? index + 1,
    gps: parseGps(item.gps),
    isReference: false,
  };
};

export function parsePlacesSpec(raw) {
  if (typeof raw !== 'string' || !raw.trim()) return null;

  try {
    const parsed = JSON.parse(raw);
    const list = Array.isArray(parsed)
      ? parsed
      : (parsed && typeof parsed === 'object' && Array.isArray(parsed.places) ? parsed.places : null);
    if (!list || list.length === 0) return null;

    const places = list.slice(0, MAX_PLACES).map(parsePlace).filter(Boolean);
    if (!places.length) return null;

    const sourceMap = (!Array.isArray(parsed) && parsed.map && typeof parsed.map === 'object')
      ? parsed.map
      : {};
    const coords = places.map((place) => place.gps).filter(Boolean);
    const center = parseGps(sourceMap.center) || (coords.length
      ? {
        lat: coords.reduce((sum, point) => sum + point.lat, 0) / coords.length,
        lng: coords.reduce((sum, point) => sum + point.lng, 0) / coords.length,
      }
      : null);

    return {
      places,
      map: {
        center,
        zoom: asFiniteNumber(sourceMap.zoom),
        staticImage: asHttpUrl(sourceMap.staticImage, 1000),
        query: asTrimmedString(sourceMap.query || (!Array.isArray(parsed) && parsed.query), 200) || null,
      },
    };
  } catch (_) {
    return null;
  }
}
